import { useCallback, useEffect, useMemo, useState } from 'react';
import { Fingerprint, Plus, Pencil, Trash2, TestTube2, ChevronDown, ChevronUp } from 'lucide-react';
import ssoService from '../services/sso';
import tenantsService from '../services/tenants';
import { useApps } from '../contexts/AppsContext';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';

const EMPTY_CONFIG = {
    provider_name: '',
    provider_slug: '',
    protocol: 'oidc',
    tenant_id: '', // '' = app-level (all tenants); set to scope to one tenant
    // OIDC
    oidc_issuer_url: '',
    oidc_client_id: '',
    oidc_client_secret: '',
    oidc_scopes: ['openid', 'email', 'profile'],
    oidc_jwks_uri: '',
    // SAML
    saml_entity_id: '',
    saml_sso_url: '',
    saml_slo_url: '',
    saml_certificate: '',
    saml_name_id_format: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
    // Common
    attribute_mapping: { email: 'email', first_name: 'given_name', last_name: 'family_name' },
    auto_create_user: 0,
    default_role_ids: [],
    // Trust + provisioning controls
    allow_account_linking: 0,
    allowed_email_domains: [],
    group_role_mapping: {},
    groups_attribute: '',
    allowed_redirect_uris: [],
    enforce_sso_only: 0,
};

const SSOPage = () => {
    const { apps, loading: loadingApps } = useApps();
    const [appId, setAppId] = useState('');

    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Tenant scoping — only relevant for multi-tenant apps
    const currentApp = apps?.find((a) => a.app_id === appId);
    const isMultiTenant = currentApp?.is_multi_tenant === 1 || currentApp?.is_multi_tenant === '1';
    const [tenants, setTenants] = useState([]);
    const [tenantFilter, setTenantFilter] = useState('all'); // 'all' | '' (app-level) | tenant_id
    const tenantMap = useMemo(() => {
        const map = new Map();
        tenants.forEach((t) => map.set(t.tenant_id, t.tenant_name));
        return map;
    }, [tenants]);
    const visibleConfigs = useMemo(() => {
        if (!isMultiTenant || tenantFilter === 'all') return configs;
        if (tenantFilter === '') return configs.filter((c) => !c.tenant_id);
        return configs.filter((c) => c.tenant_id === tenantFilter);
    }, [configs, isMultiTenant, tenantFilter]);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null); // sso_config_id or null for new
    const [form, setForm] = useState({ ...EMPTY_CONFIG });
    const [attrMappingText, setAttrMappingText] = useState('{}');
    const [scopesText, setScopesText] = useState('openid email profile');
    const [defaultRoleIdsText, setDefaultRoleIdsText] = useState('');
    const [allowedDomainsText, setAllowedDomainsText] = useState('');
    const [groupRoleMappingText, setGroupRoleMappingText] = useState('{}');
    const [allowedRedirectsText, setAllowedRedirectsText] = useState('');
    const [saving, setSaving] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [testResult, setTestResult] = useState(null);

    const fetchConfigs = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await ssoService.getConfigurations(appId);
            setConfigs(data);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to fetch SSO configurations.');
        } finally {
            setLoading(false);
        }
    }, [appId]);

    useEffect(() => {
        if (apps?.length && !appId) {
            setAppId(resolveDefaultAppId(apps));
        }
    }, [apps, appId]);

    useEffect(() => {
        fetchConfigs();
    }, [fetchConfigs]);

    useEffect(() => {
        setTenantFilter('all');
        if (!appId || !isMultiTenant) {
            setTenants([]);
            return;
        }
        tenantsService
            .getAll(appId)
            .then((data) => setTenants(data || []))
            .catch(() => setTenants([]));
    }, [appId, isMultiTenant]);

    // Clear messages after delay
    useEffect(() => {
        if (success) {
            const t = setTimeout(() => setSuccess(''), 4000);
            return () => clearTimeout(t);
        }
    }, [success]);

    const openNewForm = () => {
        setEditing(null);
        setForm({ ...EMPTY_CONFIG });
        setAttrMappingText(JSON.stringify(EMPTY_CONFIG.attribute_mapping, null, 2));
        setScopesText('openid email profile');
        setDefaultRoleIdsText('');
        setAllowedDomainsText('');
        setGroupRoleMappingText('{}');
        setAllowedRedirectsText('');
        setShowForm(true);
        setTestResult(null);
    };

    const openEditForm = (config) => {
        setEditing(config.sso_config_id);
        setForm({
            ...EMPTY_CONFIG,
            ...config,
            oidc_client_secret: '', // never returned, must re-enter to change
        });
        setAttrMappingText(config.attribute_mapping ? JSON.stringify(config.attribute_mapping, null, 2) : '{}');
        setScopesText(Array.isArray(config.oidc_scopes) ? config.oidc_scopes.join(' ') : 'openid email profile');
        setDefaultRoleIdsText(Array.isArray(config.default_role_ids) ? config.default_role_ids.join(', ') : '');
        setAllowedDomainsText(Array.isArray(config.allowed_email_domains) ? config.allowed_email_domains.join(', ') : '');
        setGroupRoleMappingText(
            config.group_role_mapping ? JSON.stringify(config.group_role_mapping, null, 2) : '{}'
        );
        setAllowedRedirectsText(
            Array.isArray(config.allowed_redirect_uris) ? config.allowed_redirect_uris.join(', ') : ''
        );
        setShowForm(true);
        setTestResult(null);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditing(null);
        setError('');
        setTestResult(null);
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        // Parse attribute_mapping JSON
        let attribute_mapping;
        try {
            attribute_mapping = JSON.parse(attrMappingText);
        } catch {
            setError('Invalid JSON in attribute mapping.');
            setSaving(false);
            return;
        }

        // Parse group->role mapping JSON
        let group_role_mapping;
        try {
            group_role_mapping = groupRoleMappingText.trim() ? JSON.parse(groupRoleMappingText) : {};
        } catch {
            setError('Invalid JSON in group → role mapping.');
            setSaving(false);
            return;
        }

        const scopes = scopesText.trim().split(/\s+/).filter(Boolean);
        const defaultRoleIds = defaultRoleIdsText
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        const allowedDomains = allowedDomainsText
            .split(',')
            .map((s) => s.trim().replace(/^@/, '').toLowerCase())
            .filter(Boolean);
        const allowedRedirects = allowedRedirectsText
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

        // Only the editable fields: an edited form also holds read-only columns from the
        // list response (created_on, has_oidc_client_secret, …), and the backend
        // rejects any key it doesn't expect.
        const editable = Object.fromEntries(Object.keys(EMPTY_CONFIG).map((k) => [k, form[k]]));
        const payload = {
            ...editable,
            tenant_id: form.tenant_id || null,
            attribute_mapping,
            oidc_scopes: scopes,
            default_role_ids: defaultRoleIds.length ? defaultRoleIds : [],
            auto_create_user: form.auto_create_user ? 1 : 0,
            allow_account_linking: form.allow_account_linking ? 1 : 0,
            allowed_email_domains: allowedDomains,
            group_role_mapping,
            groups_attribute: form.groups_attribute || '',
            allowed_redirect_uris: allowedRedirects,
            enforce_sso_only: form.enforce_sso_only ? 1 : 0,
        };

        // Remove empty secret fields so backend keeps existing values
        if (!payload.oidc_client_secret) delete payload.oidc_client_secret;
        if (!payload.saml_certificate) delete payload.saml_certificate;

        try {
            if (editing) {
                payload.sso_config_id = editing;
                await ssoService.update(payload);
                setSuccess('SSO configuration updated.');
            } else {
                await ssoService.configure({ ...payload, app_id: appId });
                setSuccess('SSO configuration created.');
            }
            closeForm();
            fetchConfigs();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save SSO configuration.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (ssoConfigId) => {
        if (!window.confirm('Are you sure you want to delete this SSO configuration?')) return;
        try {
            await ssoService.delete(ssoConfigId);
            setSuccess('SSO configuration deleted.');
            fetchConfigs();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete SSO configuration.');
        }
    };

    const handleTestConnection = async () => {
        setTestResult(null);
        if (form.protocol === 'oidc' && form.oidc_issuer_url) {
            try {
                const wellKnownUrl = `${form.oidc_issuer_url.replace(/\/$/, '')}/.well-known/openid-configuration`;
                const res = await fetch(wellKnownUrl);
                if (res.ok) {
                    const data = await res.json();
                    setTestResult({
                        ok: true,
                        message: `Connected. Issuer: ${data.issuer}, Authorization: ${
                            data.authorization_endpoint ? 'found' : 'missing'
                        }`,
                    });
                } else {
                    setTestResult({ ok: false, message: `HTTP ${res.status} from discovery endpoint.` });
                }
            } catch (err) {
                setTestResult({ ok: false, message: `Connection failed: ${err.message}` });
            }
        } else if (form.protocol === 'saml' && form.saml_sso_url) {
            try {
                // Just verify the URL is reachable (may return HTML)
                setTestResult({
                    ok: true,
                    message: `SAML SSO URL configured: ${form.saml_sso_url}. Full test requires IdP round-trip.`,
                });
            } catch (err) {
                setTestResult({ ok: false, message: `Connection failed: ${err.message}` });
            }
        } else {
            setTestResult({ ok: false, message: 'Enter an issuer URL (OIDC) or SSO URL (SAML) first.' });
        }
    };

    if (loadingApps) return <SkeletonList />;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Enterprise SSO"
                subtitle="SAML / OIDC federation"
                actions={
                    <div className="flex items-center gap-3">
                        <select
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={appId}
                            onChange={(e) => {
                                setAppId(e.target.value);
                                setSelectedAppId(e.target.value);
                            }}
                        >
                            {apps?.map((a) => (
                                <option key={a.app_id} value={a.app_id}>
                                    {a.app_name}
                                </option>
                            ))}
                        </select>
                        {isMultiTenant && (
                            <select
                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                value={tenantFilter}
                                onChange={(e) => setTenantFilter(e.target.value)}
                                title="Filter providers by tenant scope"
                            >
                                <option value="all">All scopes</option>
                                <option value="">App-level only</option>
                                {tenants.map((t) => (
                                    <option key={t.tenant_id} value={t.tenant_id}>
                                        {t.tenant_name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {!showForm && (
                            <button
                                onClick={openNewForm}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                            >
                                <Plus className="h-4 w-4" /> Add Provider
                            </button>
                        )}
                    </div>
                }
            />

            {error && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                    {error}
                </div>
            )}
            {success && (
                <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-4 py-3">
                    {success}
                </div>
            )}

            {/* ── Form ── */}
            {showForm && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6 shadow-sm">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                        {editing ? 'Edit SSO Provider' : 'Add SSO Provider'}
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Common fields */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Provider Name</label>
                                <input
                                    type="text"
                                    required
                                    value={form.provider_name}
                                    onChange={(e) => handleChange('provider_name', e.target.value)}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                    placeholder="Okta, Azure AD, PingFederate..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Provider Slug</label>
                                <input
                                    type="text"
                                    required
                                    value={form.provider_slug}
                                    onChange={(e) =>
                                        handleChange(
                                            'provider_slug',
                                            e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-')
                                        )
                                    }
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                    placeholder="okta-corp"
                                />
                                <p className="text-xs text-slate-400 mt-1">URL-safe identifier for SSO redirect</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Protocol</label>
                                <select
                                    value={form.protocol}
                                    onChange={(e) => handleChange('protocol', e.target.value)}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                >
                                    <option value="oidc">OIDC (OpenID Connect)</option>
                                    <option value="saml">SAML 2.0</option>
                                </select>
                            </div>
                        </div>

                        {isMultiTenant && (
                            <div className="bg-slate-50 rounded-lg p-4">
                                <label className="block text-sm font-medium text-slate-700 mb-1">Tenant Scope</label>
                                <select
                                    value={form.tenant_id || ''}
                                    onChange={(e) => handleChange('tenant_id', e.target.value)}
                                    disabled={!!editing}
                                    className="w-full md:w-1/2 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-slate-100 disabled:text-slate-500"
                                >
                                    <option value="">App-level (all tenants)</option>
                                    {tenants.map((t) => (
                                        <option key={t.tenant_id} value={t.tenant_id}>
                                            {t.tenant_name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-slate-500 mt-1">
                                    {editing
                                        ? "Tenant scope is set at creation and can't be changed. Delete and recreate to re-scope."
                                        : "Scope this provider to one tenant, or leave app-level to make it available to every tenant that doesn't have its own provider."}
                                </p>
                            </div>
                        )}

                        {/* Protocol-specific tabs */}
                        {form.protocol === 'oidc' ? (
                            <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                                <h3 className="text-sm font-semibold text-slate-700">OIDC Configuration</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Issuer URL
                                        </label>
                                        <input
                                            type="url"
                                            value={form.oidc_issuer_url}
                                            onChange={(e) => handleChange('oidc_issuer_url', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Client ID
                                        </label>
                                        <input
                                            type="text"
                                            value={form.oidc_client_id}
                                            onChange={(e) => handleChange('oidc_client_id', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Client Secret{' '}
                                            {editing && (
                                                <span className="text-slate-400 font-normal">
                                                    (leave blank to keep existing)
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            type="password"
                                            value={form.oidc_client_secret}
                                            onChange={(e) => handleChange('oidc_client_secret', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Scopes <span className="text-slate-400 font-normal">(space-separated)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={scopesText}
                                            onChange={(e) => setScopesText(e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            placeholder="openid email profile"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            JWKS URI{' '}
                                            <span className="text-slate-400 font-normal">
                                                (optional, auto-discovered from issuer)
                                            </span>
                                        </label>
                                        <input
                                            type="url"
                                            value={form.oidc_jwks_uri}
                                            onChange={(e) => handleChange('oidc_jwks_uri', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                                <h3 className="text-sm font-semibold text-slate-700">SAML Configuration</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Entity ID (SP)
                                        </label>
                                        <input
                                            type="text"
                                            value={form.saml_entity_id}
                                            onChange={(e) => handleChange('saml_entity_id', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            placeholder="urn:myapp:sp"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            SSO URL (IdP)
                                        </label>
                                        <input
                                            type="url"
                                            value={form.saml_sso_url}
                                            onChange={(e) => handleChange('saml_sso_url', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            placeholder="https://idp.example.com/sso/saml"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            SLO URL <span className="text-slate-400 font-normal">(optional)</span>
                                        </label>
                                        <input
                                            type="url"
                                            value={form.saml_slo_url}
                                            onChange={(e) => handleChange('saml_slo_url', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            NameID Format
                                        </label>
                                        <select
                                            value={form.saml_name_id_format}
                                            onChange={(e) => handleChange('saml_name_id_format', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        >
                                            <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">
                                                Email Address
                                            </option>
                                            <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">
                                                Persistent
                                            </option>
                                            <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">
                                                Transient
                                            </option>
                                            <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified">
                                                Unspecified
                                            </option>
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            IdP Signing Certificate (PEM)
                                            {editing && (
                                                <span className="text-slate-400 font-normal">
                                                    {' '}
                                                    (leave blank to keep existing)
                                                </span>
                                            )}
                                        </label>
                                        <textarea
                                            rows={4}
                                            value={form.saml_certificate}
                                            onChange={(e) => handleChange('saml_certificate', e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Attribute mapping */}
                        <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                            <h3 className="text-sm font-semibold text-slate-700">Attribute Mapping</h3>
                            <p className="text-xs text-slate-500">
                                Map IdP attributes/claims to user fields. Keys: email, first_name, last_name. Values:
                                the claim name from the IdP.
                            </p>
                            <textarea
                                rows={4}
                                value={attrMappingText}
                                onChange={(e) => setAttrMappingText(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-200"
                                placeholder='{"email": "email", "first_name": "given_name", "last_name": "family_name"}'
                            />
                        </div>

                        {/* Auto-provisioning */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="auto_create_user"
                                    checked={!!form.auto_create_user}
                                    onChange={(e) => handleChange('auto_create_user', e.target.checked ? 1 : 0)}
                                    className="rounded border-slate-300"
                                />
                                <label htmlFor="auto_create_user" className="text-sm font-medium text-slate-700">
                                    Auto-create users on first SSO login
                                </label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Default Role IDs{' '}
                                    <span className="text-slate-400 font-normal">(comma-separated)</span>
                                </label>
                                <input
                                    type="text"
                                    value={defaultRoleIdsText}
                                    onChange={(e) => setDefaultRoleIdsText(e.target.value)}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                    placeholder="role-id-1, role-id-2"
                                />
                            </div>
                        </div>

                        {/* Trust + group-driven roles */}
                        <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                            <h3 className="text-sm font-semibold text-slate-700">Trust &amp; Role Mapping</h3>
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="allow_account_linking"
                                    checked={!!form.allow_account_linking}
                                    onChange={(e) =>
                                        handleChange('allow_account_linking', e.target.checked ? 1 : 0)
                                    }
                                    className="rounded border-slate-300"
                                />
                                <label htmlFor="allow_account_linking" className="text-sm font-medium text-slate-700">
                                    Allow linking an IdP identity to an existing user by email
                                </label>
                            </div>
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="enforce_sso_only"
                                    checked={!!form.enforce_sso_only}
                                    onChange={(e) => handleChange('enforce_sso_only', e.target.checked ? 1 : 0)}
                                    className="rounded border-slate-300"
                                />
                                <label htmlFor="enforce_sso_only" className="text-sm font-medium text-slate-700">
                                    Enforce SSO only (disable local password login / reset / change for this scope)
                                </label>
                            </div>
                            <p className="text-xs text-slate-500 -mt-2">
                                When on, users in this app/tenant must sign in via the IdP — local password login,
                                forgot-password and change-password are rejected. Portal admins keep a break-glass
                                local login.
                            </p>
                            <p className="text-xs text-slate-500 -mt-2">
                                Leave off unless you trust this IdP to assert verified emails — otherwise an IdP
                                returning an existing user&apos;s email could hijack that account.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Allowed Email Domains{' '}
                                        <span className="text-slate-400 font-normal">(comma-separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={allowedDomainsText}
                                        onChange={(e) => setAllowedDomainsText(e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        placeholder="example.com, corp.example.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Groups Claim/Attribute
                                    </label>
                                    <input
                                        type="text"
                                        value={form.groups_attribute || ''}
                                        onChange={(e) => handleChange('groups_attribute', e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        placeholder="groups"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Group → Role Mapping{' '}
                                    <span className="text-slate-400 font-normal">(JSON)</span>
                                </label>
                                <p className="text-xs text-slate-500 mb-1">
                                    Map IdP group names to role IDs. Matching groups take precedence over default
                                    roles on provisioning.
                                </p>
                                <textarea
                                    rows={4}
                                    value={groupRoleMappingText}
                                    onChange={(e) => setGroupRoleMappingText(e.target.value)}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-200"
                                    placeholder='{"Admins": ["role-id-1"], "Field-Agents": ["role-id-2"]}'
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Allowed Redirect URIs{' '}
                                    <span className="text-slate-400 font-normal">(comma-separated)</span>
                                </label>
                                <p className="text-xs text-slate-500 mb-1">
                                    Post-login redirect targets the apps may pass: web callback URLs and the mobile
                                    deep-link scheme (e.g. <code>indeforms://sso-callback</code>). Prefix match.
                                </p>
                                <input
                                    type="text"
                                    value={allowedRedirectsText}
                                    onChange={(e) => setAllowedRedirectsText(e.target.value)}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                    placeholder="https://app.example.com/sso/callback, indeforms://sso-callback"
                                />
                            </div>
                        </div>

                        {/* Test connection */}
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={handleTestConnection}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                                <TestTube2 className="h-4 w-4" />
                                Test Connection
                            </button>
                            {testResult && (
                                <span className={`text-sm ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}>
                                    {testResult.message}
                                </span>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-5 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
                            >
                                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                            </button>
                            <button
                                type="button"
                                onClick={closeForm}
                                className="px-5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ── Configs List ── */}
            {loading ? (
                <div className="text-center py-12 text-slate-500">Loading SSO configurations...</div>
            ) : visibleConfigs.length === 0 && !showForm ? (
                <div className="text-center py-12">
                    <Fingerprint className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 mb-1">No SSO providers configured</p>
                    <p className="text-sm text-slate-400">
                        Add an OIDC or SAML provider to enable enterprise single sign-on.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {visibleConfigs.map((config) => (
                        <div
                            key={config.sso_config_id}
                            className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
                        >
                            <div
                                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50"
                                onClick={() =>
                                    setExpandedId(expandedId === config.sso_config_id ? null : config.sso_config_id)
                                }
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-lg bg-primary-50 text-primary-600 grid place-content-center">
                                        <Fingerprint className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-slate-900">{config.provider_name}</div>
                                        <div className="text-xs text-slate-500">
                                            {config.protocol.toUpperCase()} &middot; /{config.provider_slug}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {isMultiTenant && (
                                        <span
                                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                config.tenant_id
                                                    ? 'bg-indigo-50 text-indigo-700'
                                                    : 'bg-amber-50 text-amber-700'
                                            }`}
                                        >
                                            {config.tenant_id
                                                ? tenantMap.get(config.tenant_id) || 'Tenant'
                                                : 'App-level'}
                                        </span>
                                    )}
                                    <span
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            config.is_active
                                                ? 'bg-green-50 text-green-700'
                                                : 'bg-slate-100 text-slate-500'
                                        }`}
                                    >
                                        {config.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEditForm(config);
                                        }}
                                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                                        title="Edit"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(config.sso_config_id);
                                        }}
                                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                                        title="Delete"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                    {expandedId === config.sso_config_id ? (
                                        <ChevronUp className="h-4 w-4 text-slate-400" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 text-slate-400" />
                                    )}
                                </div>
                            </div>
                            {expandedId === config.sso_config_id && (
                                <div className="px-5 pb-4 border-t border-slate-100 pt-3 space-y-2 text-sm text-slate-600">
                                    {config.protocol === 'oidc' && (
                                        <>
                                            <Detail label="Issuer URL" value={config.oidc_issuer_url} />
                                            <Detail label="Client ID" value={config.oidc_client_id} />
                                            <Detail
                                                label="Client Secret"
                                                value={config.has_oidc_client_secret ? '(configured)' : '(not set)'}
                                            />
                                            <Detail
                                                label="Scopes"
                                                value={
                                                    Array.isArray(config.oidc_scopes)
                                                        ? config.oidc_scopes.join(', ')
                                                        : '-'
                                                }
                                            />
                                            <Detail
                                                label="JWKS URI"
                                                value={config.oidc_jwks_uri || '(auto-discovered)'}
                                            />
                                        </>
                                    )}
                                    {config.protocol === 'saml' && (
                                        <>
                                            <Detail label="Entity ID" value={config.saml_entity_id} />
                                            <Detail label="SSO URL" value={config.saml_sso_url} />
                                            <Detail label="SLO URL" value={config.saml_slo_url || '-'} />
                                            <Detail
                                                label="Certificate"
                                                value={config.has_saml_certificate ? '(configured)' : '(not set)'}
                                            />
                                            <Detail label="NameID Format" value={config.saml_name_id_format} />
                                        </>
                                    )}
                                    <Detail label="Auto-create users" value={config.auto_create_user ? 'Yes' : 'No'} />
                                    <Detail
                                        label="Attribute Mapping"
                                        value={
                                            config.attribute_mapping ? JSON.stringify(config.attribute_mapping) : '-'
                                        }
                                    />
                                    <div className="pt-2">
                                        <span className="text-xs text-slate-400">
                                            Login URL:{' '}
                                            <code className="bg-slate-100 px-1 rounded">
                                                /api/sso/login/{config.provider_slug}?app_id={appId}
                                                {config.tenant_id ? `&tenant_id=${config.tenant_id}` : ''}
                                            </code>
                                        </span>
                                        {config.tenant_id && (
                                            <p className="text-xs text-slate-400 mt-1">
                                                Tenant-scoped — the consuming app must pass this tenant_id (or
                                                resolve it via <code>/api/sso/discover</code>) to reach this
                                                provider instead of an app-level one.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const Detail = ({ label, value }) => (
    <div className="flex gap-2">
        <span className="font-medium text-slate-500 w-36 shrink-0">{label}:</span>
        <span className="text-slate-700 break-all">{value || '-'}</span>
    </div>
);

export default SSOPage;
